import React, { createContext, useContext, useState, useEffect } from 'react';
import { sanitizeInput, validateEmail, validatePassword, detectXSS, logSecurityEvent, ClientRateLimiter } from '../utils/security.js';

const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const [securityEvents, setSecurityEvents] = useState([]);
  const [rateLimiters, setRateLimiters] = useState({});
  
  // Initialize rate limiters for different operations
  useEffect(() => {
    setRateLimiters({
      api: new ClientRateLimiter(100, 60000), // 100 requests per minute
      auth: new ClientRateLimiter(5, 300000), // 5 requests per 5 minutes
      contact: new ClientRateLimiter(3, 300000), // 3 requests per 5 minutes
      upload: new ClientRateLimiter(10, 60000) // 10 uploads per minute
    });
  }, []);

  // Sanitize user input
  const sanitizeUserInput = (input) => {
    const sanitized = sanitizeInput(input);
    
    if (sanitized !== input) {
      logSecurityEvent('INPUT_SANITIZED', {
        original: input,
        sanitized: sanitized
      });
    }
    
    return sanitized;
  };

  // Validate user input
  const validateUserInput = (input, type = 'text') => {
    if (detectXSS(input)) {
      logSecurityEvent('XSS_ATTEMPT_DETECTED', {
        input: input,
        type: type
      });
      return { isValid: false, error: 'Potentially malicious input detected' };
    }

    switch (type) {
      case 'email':
        return {
          isValid: validateEmail(input),
          error: validateEmail(input) ? null : 'Invalid email format'
        };
      case 'password':
        const passwordValidation = validatePassword(input);
        return {
          isValid: passwordValidation.isValid,
          error: passwordValidation.isValid ? null : 'Password does not meet security requirements',
          details: passwordValidation.errors
        };
      default:
        return { isValid: true, error: null };
    }
  };

  // Check rate limiting
  const checkRateLimit = (operation = 'api') => {
    const limiter = rateLimiters[operation];
    if (!limiter) return { allowed: true, timeUntilReset: 0 };

    const allowed = limiter.canMakeRequest();
    const timeUntilReset = limiter.getTimeUntilReset();

    if (!allowed) {
      logSecurityEvent('RATE_LIMIT_EXCEEDED', {
        operation: operation,
        timeUntilReset: timeUntilReset
      });
    }

    return { allowed, timeUntilReset };
  };

  // Log security event
  const logEvent = (event, details = {}) => {
    const eventData = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      event,
      details
    };

    setSecurityEvents(prev => [...prev.slice(-99), eventData]); // Keep last 100 events
    logSecurityEvent(event, details);
  };

  // Clear security events
  const clearEvents = () => {
    setSecurityEvents([]);
  };

  // Get security summary
  const getSecuritySummary = () => {
    const now = Date.now();
    const last24Hours = securityEvents.filter(
      event => now - new Date(event.timestamp).getTime() < 24 * 60 * 60 * 1000
    );

    const eventCounts = last24Hours.reduce((acc, event) => {
      acc[event.event] = (acc[event.event] || 0) + 1;
      return acc;
    }, {});

    return {
      totalEvents: last24Hours.length,
      eventCounts,
      recentEvents: last24Hours.slice(-10)
    };
  };

  // Security monitoring for form submissions
  const monitorFormSubmission = (formData, formName) => {
    const suspiciousFields = [];
    
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string' && detectXSS(value)) {
        suspiciousFields.push(key);
      }
    });

    if (suspiciousFields.length > 0) {
      logEvent('SUSPICIOUS_FORM_SUBMISSION', {
        formName,
        suspiciousFields,
        formData: Object.keys(formData)
      });
    }
  };

  // Security monitoring for API calls
  const monitorAPICall = (url, method, data) => {
    // Check for suspicious patterns in URL
    if (url.includes('..') || url.includes('<') || url.includes('>')) {
      logEvent('SUSPICIOUS_API_CALL', {
        url,
        method,
        reason: 'Suspicious characters in URL'
      });
    }

    // Check for suspicious data
    if (data && typeof data === 'object') {
      const suspiciousData = Object.entries(data).filter(([key, value]) => 
        typeof value === 'string' && detectXSS(value)
      );

      if (suspiciousData.length > 0) {
        logEvent('SUSPICIOUS_API_DATA', {
          url,
          method,
          suspiciousFields: suspiciousData.map(([key]) => key)
        });
      }
    }
  };

  const value = {
    // Input validation and sanitization
    sanitizeUserInput,
    validateUserInput,
    
    // Rate limiting
    checkRateLimit,
    
    // Security monitoring
    logEvent,
    clearEvents,
    getSecuritySummary,
    monitorFormSubmission,
    monitorAPICall,
    
    // State
    securityEvents,
    rateLimiters
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;
